/**
 * Thẻ "Tài khoản nhận tiền" (Cấu hình → Cài đặt tiệm) tách khỏi "Đặt cọc giữ chỗ" — logic thuần, có test.
 * docs/plans/tai-khoan-nhan-tien-the-rieng.md (v0.23.2). Hai thẻ cùng gọi `POST /v1/admin/cai-dat/coc`:
 *  - thẻ tài khoản gửi trường cọc của bản VỪA ĐỌC lại + tài khoản mới (+ mã khi khai lần đầu / đổi);
 *  - thẻ cọc LUÔN gửi `taiKhoan: null` ("giữ nguyên") để không bao giờ đè tài khoản vừa đổi ở thẻ kia.
 */
import type { CauHinhCoc } from './api.ts'

export interface NhapTaiKhoan {
  bin: string
  soTaiKhoan: string
  tenTaiKhoan: string
}

/** Thân gửi `POST /v1/admin/cai-dat/coc` (mọi trường cọc bắt buộc có mặt — máy chủ `.strict()`). */
export type ThanLuuCoc = Omit<CauHinhCoc, 'nganHang'> & { maXacNhan?: string }

/** Ba ô tài khoản đã điền đủ (ngân hàng đã chọn, số và tên có chữ). */
export function nhapDayDu(nhap: NhapTaiKhoan): boolean {
  return nhap.bin !== '' && nhap.soTaiKhoan.trim() !== '' && nhap.tenTaiKhoan.trim() !== ''
}

/**
 * Lần lưu này có cần mã đăng nhập không — GƯƠNG `canMaKhiLuuTaiKhoan` của máy chủ (Q1 = A): khai LẦN ĐẦU hoặc đổi sang
 * ngân hàng / số KHÁC. Cùng ngân hàng + số, chỉ sửa chữ tên: không cần. Chưa điền đủ ba ô: không có gì để lưu.
 */
export function canMaKhiLuu(
  cu: { bin: string; soTaiKhoan: string } | null,
  nhap: NhapTaiKhoan,
): boolean {
  if (!nhapDayDu(nhap)) return false
  return cu === null || cu.bin !== nhap.bin || cu.soTaiKhoan !== nhap.soTaiKhoan.trim()
}

/** Nút "Lưu tài khoản" bấm được: đủ ba ô, và khi cần mã thì ô mã có chữ. */
export function coTheLuuTaiKhoan(
  cu: { bin: string; soTaiKhoan: string } | null,
  nhap: NhapTaiKhoan,
  ma: string,
): boolean {
  if (!nhapDayDu(nhap)) return false
  return !canMaKhiLuu(cu, nhap) || ma.trim() !== ''
}

/**
 * Chủ tiệm đang gõ dở ở thẻ "Tài khoản nhận tiền" mà CHƯA bấm "Lưu tài khoản" (v0.23.2 vòng sửa 1, B1): thẻ cọc nhìn vào đây để nói
 * "Bạn chưa bấm “Lưu tài khoản”." ngay trên nút của mình — chủ điền xong rồi bấm nhầm nút cuối trang thì biết tài khoản chưa lưu.
 * Chưa có tài khoản: bất kỳ ô nào (kể cả ô mã) có chữ. Đã có: khác bản đang lưu (cùng phép cắt khoảng trắng như lúc gửi) hoặc ô mã có chữ.
 */
export function taiKhoanGoDo(
  cu: { bin: string; soTaiKhoan: string; tenTaiKhoan: string } | null,
  nhap: NhapTaiKhoan,
  ma: string,
): boolean {
  if (ma.trim() !== '') return true
  const so = nhap.soTaiKhoan.trim()
  const ten = nhap.tenTaiKhoan.trim()
  if (cu === null) return nhap.bin !== '' || so !== '' || ten !== ''
  return nhap.bin !== cu.bin || so !== cu.soTaiKhoan || ten !== cu.tenTaiKhoan
}

/**
 * Thân lưu của thẻ tài khoản: năm trường cọc lấy từ bản VỪA ĐỌC (`api.coc.cauHinh()` ngay trước khi lưu), KHÔNG từ ô
 * đang sửa dở của thẻ cọc — chủ chưa bấm Lưu thẻ cọc thì thay đổi dở ấy không bị lưu lén, và cọc vừa sửa từ máy khác không bị đè.
 */
export function thanLuuTaiKhoan(vuaDoc: CauHinhCoc, nhap: NhapTaiKhoan, ma: string): ThanLuuCoc {
  return {
    bat: vuaDoc.bat,
    apDung: vuaDoc.apDung,
    soTienVnd: vuaDoc.soTienVnd,
    giuPhut: vuaDoc.giuPhut,
    chinhSach: vuaDoc.chinhSach,
    taiKhoan: {
      bin: nhap.bin,
      soTaiKhoan: nhap.soTaiKhoan.trim(),
      tenTaiKhoan: nhap.tenTaiKhoan.trim(),
    },
    ...(canMaKhiLuu(vuaDoc.taiKhoan, nhap) ? { maXacNhan: ma } : {}),
  }
}

/** Các ô của thẻ "Đặt cọc giữ chỗ" (không còn ô tài khoản). */
export interface FormCoc {
  bat: boolean
  apDung: CauHinhCoc['apDung']
  soTien: string
  giuPhut: number
  chinhSach: string
}

/** Thân lưu của thẻ cọc: `taiKhoan: null` = giữ nguyên tài khoản đang lưu, không bao giờ mang mã. */
export function thanLuuCoc(form: FormCoc): ThanLuuCoc {
  return {
    bat: form.bat,
    apDung: form.apDung,
    soTienVnd: form.soTien === '' ? null : form.soTien,
    giuPhut: form.giuPhut,
    chinhSach: form.chinhSach.trim() === '' ? null : form.chinhSach.trim(),
    taiKhoan: null,
  }
}

/**
 * Form thẻ cọc KHÁC bản đang lưu ở máy chủ (v0.23.2 vòng sửa 1, B1): nút "Lưu cọc" khoá khi không có gì để lưu — không còn nút luôn bật mà
 * bấm vào chỉ ghi lại đúng thứ đã có rồi báo "đã lưu". Lời dặn so sau khi cắt khoảng trắng (như `thanLuuCoc`); số tiền trống ≡ null.
 */
export function cocDaDoi(form: FormCoc, dangLuu: CauHinhCoc): boolean {
  return (
    form.bat !== dangLuu.bat ||
    form.apDung !== dangLuu.apDung ||
    form.soTien !== (dangLuu.soTienVnd ?? '') ||
    form.giuPhut !== dangLuu.giuPhut ||
    form.chinhSach.trim() !== (dangLuu.chinhSach ?? '')
  )
}

/** Thẻ cọc lưu được: số tiền chẵn nghìn; bật cọc thì cần số tiền > 0 VÀ tiệm đã có tài khoản nhận (máy chủ vẫn kiểm lại). */
export function coTheBatCoc(form: FormCoc, daCoTaiKhoan: boolean): boolean {
  const tienHopLe = form.soTien === '' || BigInt(form.soTien) % 1000n === 0n
  if (!tienHopLe) return false
  if (!form.bat) return true
  return form.soTien !== '' && BigInt(form.soTien) > 0n && daCoTaiKhoan
}
